import type {
  AugmentedWarAction,
  CellId,
  Element,
  PlayerId,
} from "@cloudnest/augmented-war-engine";

/**
 * Typed replay-event adapter for Augmented War. Fixtures are generated from the
 * engine's deterministic self-play sim (`playGame`), so every non-`init` event's
 * payload already IS the engine action (there is no `stateVersion`); this simply
 * re-types it. `init` carries a full state snapshot, handled by the runner.
 *
 * Augmented War's basic game is fully public (combat resolves with both elements
 * revealed), so replay renders canonical engine state directly — nothing to redact.
 */
export type AugmentedWarReplayEventType =
  | "init"
  | "Move"
  | "Attack"
  | "Deploy"
  | "EndTurn";

export const AUGMENTED_WAR_REPLAY_EVENT_TYPES = [
  "init",
  "Move",
  "Attack",
  "Deploy",
  "EndTurn",
] as const satisfies readonly AugmentedWarReplayEventType[];

export function normalizeAugmentedWarReplayEventType(
  eventType: string,
): AugmentedWarReplayEventType {
  if (
    (AUGMENTED_WAR_REPLAY_EVENT_TYPES as readonly string[]).includes(eventType)
  ) {
    return eventType as AugmentedWarReplayEventType;
  }
  throw new Error(`Unsupported Augmented War event type: ${eventType}`);
}

/** Convert a persisted relay event (everything except `init`) into a typed action. */
export function augmentedWarEventToAction(
  eventType: string,
  payload: Record<string, unknown>,
): AugmentedWarAction {
  const type = normalizeAugmentedWarReplayEventType(eventType);
  const playerId = payload.playerId as PlayerId;

  switch (type) {
    case "init":
      throw new Error(
        "augmentedWarEventToAction cannot convert the init event",
      );
    case "Move":
      return {
        type: "Move",
        playerId,
        from: payload.from as CellId,
        to: payload.to as CellId,
      };
    case "Attack":
      return {
        type: "Attack",
        playerId,
        from: payload.from as CellId,
        target: payload.target as CellId,
        attackerElement: payload.attackerElement as Element,
        defenderElement: payload.defenderElement as Element,
        advance: payload.advance as boolean | undefined,
        attackerUseBastion: payload.attackerUseBastion as boolean | undefined,
        defenderUseBastion: payload.defenderUseBastion as boolean | undefined,
      };
    case "Deploy":
      return { type: "Deploy", playerId, to: payload.to as CellId };
    case "EndTurn":
      return { type: "EndTurn", playerId };
  }
}
